import React from 'react';
import { Easing, interpolate, useCurrentFrame, useVideoConfig } from 'remotion';
import { Caption, EmojiMap, VideoTheme, Word as WordType } from '../../types';
import { EmojiDisplay } from './EmojiDisplay';
import { Word } from './Word';

interface Horimozi1Props {
  caption: Caption;
  currentFrame: number;
  theme: VideoTheme;
  fontSize: number;
  captionPosY: number;
  highlightColors: string[];
  gap: number;
  emojiAnimationData: any;
}

export const Horimozi1: React.FC<Horimozi1Props> = ({
  caption,
  currentFrame,
  theme,
  fontSize,
  captionPosY,
  highlightColors,
  gap,
  emojiAnimationData,
}) => {
  const { fps } = useVideoConfig();
  const { emoji, words } = caption;

  const frame = useCurrentFrame();

  const scale = interpolate(frame, [0, 5, 8], [0.5, 1.2, 1], {
    extrapolateRight: 'clamp',
    extrapolateLeft: 'clamp',
    easing: Easing.bezier(0.08, 0.68, 0.74, 0.71),
  });

  return (
    <div
      style={{
        ...styles.container,
        bottom: `${captionPosY}px`,
        transform: `scale(${scale})`,
        gap: `${gap}px`,
      }}
    >
      <EmojiDisplay emoji={emoji} theme={theme} emojiAnimationData={emojiAnimationData} />
      <WordList
        words={words}
        currentFrame={currentFrame}
        fps={fps}
        highlightColors={highlightColors}
        theme={theme}
        fontSize={fontSize}
      />
    </div>
  );
};

// --------------------------------------------------------------------------------

interface WordListProps {
  words: WordType[];
  currentFrame: number;
  fps: number;
  highlightColors: string[];
  theme: VideoTheme;
  fontSize: number;
}

const WordList: React.FC<WordListProps> = ({
  words,
  currentFrame,
  fps,
  highlightColors,
  theme,
  fontSize,
}) => (
  <>
    {words.map((wordObj, index) => {
      const { word, highlightColorId, lineBreak } = wordObj;

      const highLightColor = highlightColorId ? highlightColors[Number(highlightColorId)] : '';

      return (
        <React.Fragment key={index}>
          {lineBreak && <div style={{ flex: '100%' }} />}
          <Word
            word={word}
            color={highLightColor || theme.fontStyle.fontColor}
            fontSize={fontSize}
            theme={theme}
            customWordStyle={{
              textShadow:
                '0 0 0.15em #000, 0 0 0.25em #000, 0 0 0.35em #000, 0 0 0.45em #000, 0 0 0.55em #000',
            }}
          />
        </React.Fragment>
      );
    })}
  </>
);

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    position: 'absolute',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'row',
    flexWrap: 'wrap',
    top: undefined,
    lineHeight: '1',
    width: '100%',
    padding: '0px 15px',
  },
};
